import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { lastValueFrom, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { VibrationsChart } from '../../models/charts/VibrationsChart';
import { ApiResponse } from '../../models/api-response';
import { VibrationRecords } from '@shared/models/vibrations-records';
import { VibrationRecord } from '@shared/models/vibration-record';
import { Records } from '@shared/models/records';
import { AssignedRecords } from '@shared/models/assigned-records';
import { PonderationEnum } from '@shared/models/ponderation.enum';
import { Country } from '@shared/models/country';
import { VibrationRecordFilter } from '@shared/models/vibration-record-filter';
import { StandardGraphDataDto } from '@shared/models/charts/standard-graph-data';

@Injectable({
  providedIn: 'root'
})
export class VibrationsRecordService {

  private baseUrl = environment.vibrationApiURL;

  constructor(private httpClient: HttpClient) { }

  public UploadFilenRecords(
    siteId: number,
    data: FormData

  ): Observable<any> {
    return this.httpClient.post(`${this.baseUrl}/sites/${siteId}/vibration-records/record-files`, data, {
      reportProgress: true,
      observe: 'events'
    });
  }

  public SaveRecords(
    siteId: number,
    recordsData: VibrationRecords

  ): Observable<ApiResponse<boolean>> {
    return this.httpClient.post<ApiResponse<boolean>>(`${this.baseUrl}/sites/${siteId}/vibration-records/record-data`, recordsData);
  }
  public assignRecords(
    siteId: number,
    recordsData: AssignedRecords

  ): Observable<ApiResponse<boolean>> {
    return this.httpClient.post<ApiResponse<boolean>>(`${this.baseUrl}/sites/${siteId}/vibration-records/assign`, recordsData);
  }

public GetRecords(
  siteId: number,
  eventId: number,
  blastId: number,
  controlPointId: number = null,
  device: string = null,
  pageNumber: number = null,
  pageSize: number = null,
  includeSignal: boolean,
  withCompanyFilter: boolean,
  forMultiAssign: boolean,
  controlPointsIds: number[] | null = null,
  eventsIds: number[] | null = null,
  startDate: string | null = null,
  endDate: string | null = null,
  hasGpsCoordinates: boolean = false,
  note: string | null = null
): Observable<ApiResponse<Records>> {
  const filter: VibrationRecordFilter = {
    siteId,
    eventId,
    blastId,
    controlPointId,
    device,
    pageNumber,
    pageSize,
    includeSignal,
    withCompanyFilter,
    forMultiAssign,
    controlPointsIds,
    eventsIds,
    startDate,
    endDate,
    hasGpsCoordinates,
    note
  };

  return this.httpClient.put<ApiResponse<Records>>(`${this.baseUrl}/sites/${siteId}/vibration-records`, filter);
}

  public GetRecordById(
    siteId: number,
    recordId: number,
    ponderationId: number
  ): Observable<ApiResponse<VibrationsChart>> {

    let params = new HttpParams();
    params = params.append('includeMetadata', true);
    params = params.append('includeSignal', true);
   if (ponderationId != null && !isNaN(ponderationId)) {
    params = params.append('ponderationId', ponderationId);
  }

    return this.httpClient.get<ApiResponse<VibrationsChart>>(`${this.baseUrl}/sites/${siteId}/vibration-records/${recordId}`, { params });
  }

  public updateRecord(
    siteId: number,
    recordId: string,
    recordData: VibrationRecord
  ): Observable<ApiResponse<boolean>> {
    return this.httpClient.put<ApiResponse<boolean>>(`${this.baseUrl}/sites/${siteId}/vibration-records/${recordId}`, recordData);
  }


  async deleteRecords(siteId: number, id: string): Promise<ApiResponse<boolean>> {
    return await lastValueFrom(this.httpClient.delete<ApiResponse<boolean>>(
      `${this.baseUrl}/sites/${siteId}/vibration-records/${id}`
    ));
  }

  async deleteRecordsBulk(siteId: number, ids: number[]): Promise<ApiResponse<number>> {
    return await lastValueFrom(this.httpClient.delete<ApiResponse<number>>(
      `${this.baseUrl}/sites/${siteId}/vibration-records`,
      { body: ids }
    ));
  }

  public downloadFile(recordId: number): Observable<any> {
    return this.httpClient.get(`${this.baseUrl}/sftpgo/download/${recordId}`, { responseType: 'blob', observe: 'response' });
  }

  getDownloadUrl(fileKey: string): Observable<{ downloadUrl: string }> {
    const params = [
      `fileKey=${encodeURIComponent(fileKey)}`
    ].join('&');
    return this.httpClient.get<{ downloadUrl: string }>(`${this.baseUrl}/Device/download-url?${params}`);
  }

  public getStandardGraphData(
    siteId: number,
    recordId: number,
    standardCode: string
  ): Observable<ApiResponse<StandardGraphDataDto>> {
    const params = new HttpParams().set('standardCode', standardCode);
    return this.httpClient.get<ApiResponse<StandardGraphDataDto>>(
      `${this.baseUrl}/sites/${siteId}/vibration-records/${recordId}/standard-graph`,
      { params }
    );
  }
}
